import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { miniGamesAPI } from '@/lib/api';
import { ArrowLeft, Play, Pause, RotateCcw, Trophy, Medal } from 'lucide-react';

const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 400;
const PADDLE_WIDTH = 10;
const PADDLE_HEIGHT = 80;
const BALL_SIZE = 10;
const PADDLE_SPEED = 3;
const INITIAL_BALL_SPEED = 2;

export default function PongGamePage() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [playerY, setPlayerY] = useState(CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2);
  const [cpuY, setCpuY] = useState(CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2);
  const [ballPos, setBallPos] = useState({ x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 });
  const [ballVel, setBallVel] = useState({ x: INITIAL_BALL_SPEED, y: INITIAL_BALL_SPEED });
  const [gameOver, setGameOver] = useState(false);
  const [gamePaused, setGamePaused] = useState(true);
  const [score, setScore] = useState(0);
  const [cpuScore, setCpuScore] = useState(0);
  const [personalBest, setPersonalBest] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const keysPressed = useRef({});
  const gameLoopRef = useRef(null);

  useEffect(() => {
    fetchLeaderboard();
    fetchPersonalBest();

    // Auto-refresh leaderboard every 30 seconds
    const interval = setInterval(() => {
      fetchLeaderboard();
      fetchPersonalBest();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await miniGamesAPI.getLeaderboard('pong', 10);
      setLeaderboard(response.data.scores || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const fetchPersonalBest = async () => {
    try {
      const response = await miniGamesAPI.getPersonalBest('pong');
      setPersonalBest(response.data.best_score || 0);
    } catch (error) {
      console.error('Error fetching personal best:', error);
    }
  };

  const resetBall = () => {
    setBallPos({ x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 });
    setBallVel({
      x: (Math.random() > 0.5 ? 1 : -1) * INITIAL_BALL_SPEED,
      y: (Math.random() - 0.5) * INITIAL_BALL_SPEED,
    });
  };

  // Game loop
  useEffect(() => {
    if (gamePaused || gameOver) {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
      return;
    }

    const gameLoop = () => {
      // Move player paddle
      setPlayerY(prev => {
        let newY = prev;
        if (keysPressed.current['ArrowUp']) {
          newY = Math.max(0, prev - PADDLE_SPEED);
        }
        if (keysPressed.current['ArrowDown']) {
          newY = Math.min(CANVAS_HEIGHT - PADDLE_HEIGHT, prev + PADDLE_SPEED);
        }
        return newY;
      });

      // Move CPU paddle (AI)
      setCpuY(prev => {
        const cpuCenter = prev + PADDLE_HEIGHT / 2;
        const ballCenter = ballPos.y;
        const diff = ballCenter - cpuCenter;

        if (Math.abs(diff) > 2) {
          if (diff > 0) {
            return Math.min(CANVAS_HEIGHT - PADDLE_HEIGHT, prev + PADDLE_SPEED * 0.7);
          } else {
            return Math.max(0, prev - PADDLE_SPEED * 0.7);
          }
        }
        return prev;
      });

      // Move ball
      setBallPos(prev => {
        let newX = prev.x + ballVel.x;
        let newY = prev.y + ballVel.y;

        // Top/bottom collision
        if (newY <= 0 || newY >= CANVAS_HEIGHT - BALL_SIZE) {
          setBallVel(vel => ({ ...vel, y: -vel.y }));
          newY = Math.max(0, Math.min(CANVAS_HEIGHT - BALL_SIZE, newY));
        }

        // Player paddle collision
        if (
          newX <= PADDLE_WIDTH &&
          newY + BALL_SIZE >= playerY &&
          newY <= playerY + PADDLE_HEIGHT
        ) {
          setBallVel(vel => ({
            x: Math.abs(vel.x) * 1.03,
            y: vel.y + (Math.random() - 0.5) * 1,
          }));
          newX = PADDLE_WIDTH;
          setScore(prev => prev + 1);
        }

        // CPU paddle collision
        if (
          newX + BALL_SIZE >= CANVAS_WIDTH - PADDLE_WIDTH &&
          newY + BALL_SIZE >= cpuY &&
          newY <= cpuY + PADDLE_HEIGHT
        ) {
          setBallVel(vel => ({
            x: -Math.abs(vel.x) * 1.03,
            y: vel.y + (Math.random() - 0.5) * 1,
          }));
          newX = CANVAS_WIDTH - PADDLE_WIDTH - BALL_SIZE;
        }

        // Player scores (CPU misses)
        if (newX >= CANVAS_WIDTH) {
          setScore(prev => prev + 10);
          resetBall();
          return { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 };
        }

        // CPU scores (player misses)
        if (newX <= 0) {
          setCpuScore(prev => {
            const newCpuScore = prev + 1;
            if (newCpuScore >= 5) {
              setGameOver(true);
            }
            return newCpuScore;
          });
          resetBall();
          return { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 };
        }

        return { x: newX, y: newY };
      });

      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoopRef.current = requestAnimationFrame(gameLoop);
    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
  }, [gamePaused, gameOver, ballVel, playerY, cpuY, ballPos]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      keysPressed.current[e.key] = true;

      if (e.key === ' ') {
        e.preventDefault();
        setGamePaused(prev => !prev);
      }

      if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && gamePaused && !gameOver) {
        setGamePaused(false);
      }
    };

    const handleKeyUp = (e) => {
      keysPressed.current[e.key] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gamePaused, gameOver]);

  // Draw game
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Center line
    ctx.strokeStyle = '#444';
    ctx.setLineDash([10, 10]);
    ctx.beginPath();
    ctx.moveTo(CANVAS_WIDTH / 2, 0);
    ctx.lineTo(CANVAS_WIDTH / 2, CANVAS_HEIGHT);
    ctx.stroke();
    ctx.setLineDash([]);

    // Player paddle (left)
    ctx.fillStyle = '#a855f7';
    ctx.fillRect(0, playerY, PADDLE_WIDTH, PADDLE_HEIGHT);

    // CPU paddle (right)
    ctx.fillStyle = '#ef4444';
    ctx.fillRect(CANVAS_WIDTH - PADDLE_WIDTH, cpuY, PADDLE_WIDTH, PADDLE_HEIGHT);

    // Ball
    ctx.fillStyle = '#fff';
    ctx.fillRect(ballPos.x, ballPos.y, BALL_SIZE, BALL_SIZE);

    // Scores
    ctx.fillStyle = '#a855f7';
    ctx.font = 'bold 32px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(score, CANVAS_WIDTH / 4, 50);

    ctx.fillStyle = '#ef4444';
    ctx.fillText(cpuScore, (CANVAS_WIDTH * 3) / 4, 50);

    // Game over overlay
    if (gameOver) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Game Over!', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 20);
      ctx.font = '24px Arial';
      ctx.fillText(`Score: ${score}`, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 20);
    }
  }, [playerY, cpuY, ballPos, score, cpuScore, gameOver]);

  const handleRestart = () => {
    setPlayerY(CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2);
    setCpuY(CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2);
    resetBall();
    setGameOver(false);
    setGamePaused(true);
    setScore(0);
    setCpuScore(0);
  };

  const handleSubmitScore = async () => {
    try {
      setSubmitting(true);
      await miniGamesAPI.submitScore('pong', score);
      await fetchLeaderboard();
      await fetchPersonalBest();
      handleRestart();
    } catch (error) {
      console.error('Error submitting score:', error);
      alert('Fout bij opslaan score: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
    });
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" onClick={() => navigate('/mini-games')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Terug
        </Button>
        <div>
          <h1 className="text-3xl font-bold">🏓 Pong</h1>
          <p className="text-gray-400">Klassiek ping-pong tegen AI!</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card className="border-purple-500/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex gap-6">
                  <div>
                    <span className="text-purple-400">Jij</span>: <span className="font-bold">{score}</span>
                  </div>
                  <div>
                    <span className="text-red-400">CPU</span>: <span className="font-bold">{cpuScore}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {!gameOver && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setGamePaused(!gamePaused)}
                    >
                      {gamePaused ? (
                        <><Play className="w-4 h-4 mr-2" /> Start</>
                      ) : (
                        <><Pause className="w-4 h-4 mr-2" /> Pauze</>
                      )}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={handleRestart}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Opnieuw
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <canvas
                  ref={canvasRef}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                  className="border-2 border-purple-500/50 rounded"
                />
              </div>

              <div className="mt-4 p-4 bg-gray-800/50 rounded-lg">
                <h3 className="font-semibold mb-2">Besturing:</h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-400">
                  <div>• Pijl omhoog: paddle omhoog</div>
                  <div>• Pijl omlaag: paddle omlaag</div>
                  <div>• Spatiebalk: pauze</div>
                  <div>• Eerste tot 5 punten verliest!</div>
                </div>
              </div>

              {gameOver && (
                <div className="mt-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
                  <p className="text-center mb-3">
                    {score > personalBest ? (
                      <span className="text-purple-400 font-bold">Nieuwe persoonlijke record!</span>
                    ) : (
                      <span>Je score: {score}</span>
                    )}
                  </p>
                  <Button
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={handleSubmitScore}
                    disabled={submitting}
                  >
                    {submitting ? 'Opslaan...' : 'Score Opslaan'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-purple-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Medal className="w-5 h-5 text-purple-400" />
                Persoonlijk Record
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-purple-400">{personalBest}</p>
            </CardContent>
          </Card>

          <Card className="border-purple-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-400" />
                Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {leaderboard.length === 0 ? (
                  <p className="text-gray-400 text-center py-4">Nog geen scores</p>
                ) : (
                  leaderboard.map((entry, index) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-2 bg-gray-800/50 rounded"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className={index < 3 ? 'text-yellow-400' : ''}>
                          #{index + 1}
                        </Badge>
                        <div>
                          <p className="font-semibold">{entry.username}</p>
                          <p className="text-xs text-gray-400">
                            {formatDate(entry.created_at)}
                          </p>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-purple-400">{entry.score}</span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
